// src/pages/orders/components/EcomOrderPaymentRecordsModal.jsx
//
// "Payments & Shift" modal for in-courier ecom orders — the shared
// src/components/common/PaymentRecordsModal driven by PATCH /ecom-orders/:id/in-courier-adjustments,
// which only accepts paymentTypeKey, paymentRecords (full array) and messageCenterShiftId.
import React, { useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import {
  getEcomOrder,
  updateInCourierAdjustments,
  uploadOrderPaymentProof,
} from '../../../services/api/orders';
import { getPaymentMethods, getPaymentTypes } from '../../../services/api/orderReference';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useBusinessSettings } from '../../../hooks/useBusinessSettings';
import { formatCurrency } from '../../../utils/currencyUtil';
import PaymentRecordsModal from '../../../components/common/PaymentRecordsModal/PaymentRecordsModal';
import Dropdown from '../../../components/ui/Dropdown/Dropdown';
import ShiftPickerSection from './ShiftPickerSection';
import styles from './EcomOrderPaymentRecordsModal.module.css';

const EXCLUDED_METHOD_KEYS = ['debit_note', 'credit_note'];
const ADJUSTABLE_PAYMENT_TYPE_KEYS = ['fully_paid', 'partially_paid'];

const normalizeRecord = (record) => ({ ...record, paymentMethod: record.PaymentMethod });

const toPayload = (record) => ({
  id: record.id,
  paymentMethodKey: record.PaymentMethod?.key || record.paymentMethodKey,
  paymentAmount: parseFloat(record.paymentAmount),
  reference: record.reference || '',
  paymentDate: record.paymentDate,
  proofUrl: record.proofUrl || null,
});

const sumPaid = (records) => records.reduce((sum, r) => sum + (parseFloat(r.paymentAmount) || 0), 0);

const EcomOrderPaymentRecordsModal = ({ order, onClose, onSaved, canEditPayments = true, canEditShift = true }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const { settings: messageCenterSettings } = useBusinessSettings('messageCenter');

  const [currentOrder, setCurrentOrder] = useState(order);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentTypeKey, setPaymentTypeKey] = useState(order.PaymentType?.key || '');
  const [shiftId, setShiftId] = useState(order.shiftMap?.shiftId || null);
  const [isSavingType, setIsSavingType] = useState(false);
  const [isSavingShift, setIsSavingShift] = useState(false);

  useEffect(() => {
    getPaymentTypes()
      .then((data) => setPaymentTypes((data.paymentTypes || []).filter((p) => ADJUSTABLE_PAYMENT_TYPE_KEYS.includes(p.key))))
      .catch(() => setPaymentTypes([]));
  }, [order]);

  const isShopShiftEligible = !!order.Shop?.messageCenterShiftEligible;

  const reloadOrder = async (orderId) => {
    const { order: freshOrder } = await getEcomOrder(orderId);
    setCurrentOrder(freshOrder);
    return freshOrder;
  };

  const adapter = useMemo(() => buildAdapter(), [t, currentOrder, paymentTypeKey, paymentTypes, isSavingType, isSavingShift, shiftId, isShopShiftEligible, messageCenterSettings.enabled, canEditPayments, canEditShift]); // eslint-disable-line react-hooks/exhaustive-deps

  function buildAdapter() {
    const saveRecords = async (records, extra = {}) => {
      await updateInCourierAdjustments(currentOrder.id, {
        paymentTypeKey,
        paymentRecords: records,
        ...extra,
      });
      return reloadOrder(currentOrder.id);
    };
    const existingPayload = () => (currentOrder.paymentRecords || []).map(toPayload);
    const isFullyPaid = paymentTypeKey === 'fully_paid';
    const isLocked = (currentOrder.paymentRecords || []).some((r) => r.isVerified);
    const grandTotal = parseFloat(currentOrder.grandTotal) || 0;

    return {
      inputIdPrefix: 'eco',
      canEditRecords: canEditPayments,
      getNumber: (entity) => entity.orderNumber,
      getBalance: (entity) => Math.max(0, (parseFloat(entity.grandTotal) || 0) - sumPaid(entity.paymentRecords || [])),
      getPartyId: () => null,
      loadRecords: async (orderId) => ((await reloadOrder(orderId)).paymentRecords || []).map(normalizeRecord),
      loadMethods: async () => {
        const data = await getPaymentMethods();
        return (data.paymentMethods || []).filter((m) => !EXCLUDED_METHOD_KEYS.includes(m.key));
      },
      uploadProof: uploadOrderPaymentProof,
      updateRecord: async (id, data) => {
        const records = existingPayload().map((r) => (r.id === id ? { ...r, ...data } : r));
        const freshOrder = await saveRecords(records);
        return normalizeRecord((freshOrder.paymentRecords || []).find((r) => r.id === id) || {});
      },
      deleteRecord: (record) => saveRecords(existingPayload().filter((r) => r.id !== record.id)),
      validateDrafts: (drafts) => {
        if (!ADJUSTABLE_PAYMENT_TYPE_KEYS.includes(paymentTypeKey)) return t('ecomOrders.selectPaidPaymentTypeFirst');
        const totalPaid = sumPaid(currentOrder.paymentRecords || []) + drafts.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
        const params = { totalPaid: formatCurrency(totalPaid), grandTotal: formatCurrency(grandTotal) };
        if (isFullyPaid && Math.abs(totalPaid - grandTotal) > 0.01) {
          return totalPaid < grandTotal
            ? t('ecomOrders.validation.fullyPaidIncomplete', params)
            : t('ecomOrders.validation.fullyPaidOverpaid', params);
        }
        if (paymentTypeKey === 'partially_paid' && totalPaid >= grandTotal) {
          return t('ecomOrders.validation.partiallyPaidInvalid', params);
        }
        return null;
      },
      recordPayments: (orderId, payments) => saveRecords([
        ...existingPayload(),
        ...payments.map((p) => ({
          paymentMethodKey: p.paymentMethodKey,
          paymentAmount: p.amount,
          reference: p.reference || '',
          paymentDate: p.paymentDate,
          proofUrl: p.proofUrl,
        })),
      ]),
      reloadEntity: reloadOrder,
      verification: { canVerify: false },
      disableNewPayments: !canEditPayments || !ADJUSTABLE_PAYMENT_TYPE_KEYS.includes(paymentTypeKey),
      disableNewPaymentsReason: canEditPayments ? t('ecomOrders.selectPaidPaymentTypeFirst') : null,
      renderExtraSection() {
        return (
          <div className={styles.adjustGrid}>
            {this.shiftSection()}
            {this.paymentTypeSection()}
          </div>
        );
      },
      paymentTypeSection: () => (canEditPayments && (
        <div className={styles.adjustSection}>
          <label className={styles.adjustLabel}>{t('ecomOrders.paymentType')}</label>
          <div className={styles.adjustRow}>
            <Dropdown
              className={styles.adjustDropdown}
              value={paymentTypeKey}
              disabled={isLocked || isSavingType}
              allowClear={false}
              onChange={(value) => value && setPaymentTypeKey(value)}
              options={[
                ...(!ADJUSTABLE_PAYMENT_TYPE_KEYS.includes(paymentTypeKey)
                  ? [{ value: paymentTypeKey, label: currentOrder.PaymentType?.label || paymentTypeKey }]
                  : []),
                ...paymentTypes.map((p) => ({ value: p.key, label: p.label })),
              ]}
            />
            {paymentTypeKey !== currentOrder.PaymentType?.key && !isLocked && (
              <button
                type="button"
                className={styles.adjustSaveButton}
                disabled={isSavingType}
                onClick={async () => {
                  const typeError = adapter.validateDrafts([]);
                  if (typeError) {
                    showError(typeError);
                    return;
                  }
                  setIsSavingType(true);
                  try {
                    await saveRecords(existingPayload());
                    success(t('ecomOrders.paymentTypeUpdatedSuccess'));
                    onSaved?.();
                  } catch (err) {
                    showError(err.response?.data?.message || t('ecomOrders.failedToUpdateOrder'));
                  } finally {
                    setIsSavingType(false);
                  }
                }}
              >
                <Save size={14} /> {t('ecomOrders.save')}
              </button>
            )}
          </div>
          {isLocked && <span className={styles.adjustHint}>{t('ecomOrders.paymentTypeLockedHint')}</span>}
        </div>
      )),
      shiftSection: () => (canEditShift && messageCenterSettings.enabled && isShopShiftEligible ? (
        <div className={styles.adjustSection}>
          <label className={styles.adjustLabel}>{t('ecomOrders.messageCenterShift')}</label>
          <div className={styles.adjustRow}>
            <ShiftPickerSection
              value={shiftId}
              onChange={setShiftId}
              rememberable={true}
              autoSelectActiveShift={false}
              initialDate={order.shiftMap?.shift?.shiftDate || null}
            />
            {shiftId !== (currentOrder.shiftMap?.shiftId || null) && (
              <button
                type="button"
                className={styles.adjustSaveButton}
                disabled={isSavingShift || !shiftId}
                onClick={async () => {
                  setIsSavingShift(true);
                  try {
                    await updateInCourierAdjustments(currentOrder.id, { messageCenterShiftId: shiftId });
                    await reloadOrder(currentOrder.id);
                    success(t('ecomOrders.shiftUpdatedSuccess'));
                    onSaved?.();
                  } catch (err) {
                    showError(err.response?.data?.message || t('ecomOrders.failedToUpdateOrder'));
                  } finally {
                    setIsSavingShift(false);
                  }
                }}
              >
                <Save size={14} /> {t('ecomOrders.saveShift')}
              </button>
            )}
          </div>
        </div>
      ) : null),
      texts: {
        title: t('ecomOrders.editPaymentsAndShift'),
        outstandingBalance: t('ecomOrders.outstandingBalance'),
        amount: t('ecomOrders.paymentAmount'),
        reference: t('ecomOrders.paymentReference'),
        confirmPayment: t('ecomOrders.confirmPayment'),
        someFieldsRequired: t('ecomOrders.someFieldsRequired'),
        paymentRecordedSuccess: t('ecomOrders.paymentRecordedSuccess'),
        failedToRecordPayment: t('ecomOrders.failedToRecordPayment'),
      },
    };
  }

  return <PaymentRecordsModal entity={currentOrder} adapter={adapter} onClose={onClose} onSaved={onSaved} />;
};

export default EcomOrderPaymentRecordsModal;
